<script setup lang="ts">
import { computed } from 'vue'
import { useSpecialCollectibleChallenge } from '@/composables/game/challenges'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import CollectibleBannerBg from '@/assets/images/challenges/collectible/collectible-challenge-banner.webp'
import CollectibleBannerText from '@/assets/images/challenges/collectible/collectible-challenge-banner-text.webp'
import HolidayOrbBannerBg from '@/assets/images/challenges/holiday-orb/holiday-orb-challenge-banner.webp'
import HolidayOrbBannerText from '@/assets/images/challenges/holiday-orb/holiday-orb-challenge-banner-text.webp'

withDefaults(defineProps<{
  pulsate?: boolean
}>(), {
  pulsate: false,
})

const { specialCollectibleChallengeCurrency } = useSpecialCollectibleChallenge()

const isHolidayOrb = computed(() => specialCollectibleChallengeCurrency.value === SPECIAL_CURRENCY_ID.HOLIDAY_ORB)
const bannerBg = computed(() => isHolidayOrb.value ? HolidayOrbBannerBg : CollectibleBannerBg)
const bannerText = computed(() => isHolidayOrb.value ? HolidayOrbBannerText : CollectibleBannerText)
</script>

<template>
  <div
    class="w-max h-full relative overflow-hidden"
    :class="pulsate && 'scale-up'"
  >
    <div class="absolute top-1.5 right-1.5 h-max w-max rounded-[38px] p-[1px] live-chip-border flex items-center justify-center z-[1]">
      <div class="flex items-center gap-x-1 bg-[#010101] rounded-[38px] px-1 py-0.5 w-max">
        <div class="bg-green-primary rounded-full size-1 blink-in-out" />
        <span class="uppercase text-white font-bold text-[10px] leading-[10px] -tracking-[0.2px] w-5">Live</span>
      </div>
    </div>
    <div class="flex items-center justify-center w-full h-full relative overflow-hidden">
      <img
        class="w-auto h-full object-cover"
        :src="bannerBg"
        alt="Collectible Challenge Banner"
      >
      <img
        class="absolute left-1/2 -translate-x-1/2 z-[1] h-full w-auto"
        :src="bannerText"
        alt="Collectible Challenge"
      >
    </div>
  </div>
</template>

<style scoped>
.scale-up {
  animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.03);
  }
}

.blink-in-out {
  animation: blink-in-out 0.75s ease-in-out infinite;
}

@keyframes blink-in-out {
  0% {
    opacity: 0; /* Start completely invisible */
  }
  30% {
    opacity: 1; /* Fade in */
  }
  70% {
    opacity: 1; /* Fade in */
  }
  90% {
    opacity: 0; /* Stay fully visible */
  }
  100% {
    opacity: 0; /* Fade out */
  }
}

.live-chip-border {
  background: linear-gradient(#A8CCB1, rgba(255, 255, 255, 0));
}

.banner-title {
  text-align: center;
  text-shadow: 0 0 19.473px rgba(0, 0, 0, 0.16);
  -webkit-text-stroke-width: 3px;
  -webkit-text-stroke-color: #000;
  font-size: 32px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%; /* 32px */

  filter: drop-shadow(0px 0px 10.433px rgba(0, 0, 0, 0.66));
  background: var(--Lingogold, linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>